import { DEFAULT_CURRENCY, forecastStartDate, pickT } from "@repo/shared";
import { ForecastChart } from "@/components/charts/forecast-chart";
import { useBrandColor } from "@/lib/context/brand-color-context";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { PortfolioForecastSnapshot } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./portfolio-forecast-widget.i18n";

type PortfolioForecastWidgetProps = {
  snapshot: PortfolioForecastSnapshot;
  showStatistics?: boolean;
  showTable?: boolean;
  compareWithBank?: boolean;
  language?: string;
};

export function PortfolioForecastWidget({
  snapshot,
  showStatistics = true,
  showTable = true,
  compareWithBank = false,
  language = "en_GB",
}: PortfolioForecastWidgetProps) {
  const primaryColor = useBrandColor();
  const { formatNumber } = useRegionalFormat();
  const t = pickT(T, language);

  if (!snapshot.forecast || snapshot.forecast.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noData}</p>
      </div>
    );
  }

  // Transform data for the chart
  // Prepend initial point where all lines start at the same value (initial deposit)
  const firstEntry = snapshot.forecast[0];
  const initialDeposit = parseFloat(firstEntry.deposit);

  // Synthetic start point one month before the first entry (previous month-end).
  const startDateStr = forecastStartDate(firstEntry.date);

  // Bank comparison is display-gated by component config AND requires the
  // snapshot to carry the series (older snapshots don't until regenerated).
  const showBank = compareWithBank && !!snapshot.bank && snapshot.bank.length > 0;

  const chartData = [
    // Initial point - all lines start at the same value
    {
      date: startDateStr,
      forecast: initialDeposit,
      lower: initialDeposit,
      upper: initialDeposit,
      ...(showBank ? { bank: initialDeposit } : {}),
    },
    // API data points
    ...snapshot.forecast.map((entry, index) => ({
      date: entry.date,
      forecast: parseFloat(entry.value),
      lower: parseFloat(snapshot.lower[index]?.value || entry.value),
      upper: parseFloat(snapshot.upper[index]?.value || entry.value),
      ...(showBank ? { bank: parseFloat(snapshot.bank![index]?.value || entry.value) } : {}),
    })),
  ];

  // Format numbers for display
  const formatPercent = (value: number) => `${value.toFixed(2)}%`;
  const formatCurrency = (value: number) =>
    `${formatNumber(value, { maximumFractionDigits: 0 })} ${snapshot.currency || DEFAULT_CURRENCY}`;

  // Get final projected values
  const lastEntry = chartData[chartData.length - 1];

  return (
    <div className="w-full space-y-4">
      <div className="w-full">
        <ForecastChart
          data={chartData}
          height={280}
          primaryColor={primaryColor ?? undefined}
          lang={language}
          bankReturnPct={showBank ? snapshot.bankReturnPct : undefined}
        />
      </div>

      {showTable && lastEntry && (
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">{t.lower}</p>
            <p className="font-semibold">{formatCurrency(lastEntry.lower)}</p>
          </div>
          <div className="bg-primary/10 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">{t.expected}</p>
            <p className="font-semibold text-primary">{formatCurrency(lastEntry.forecast)}</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">{t.upper}</p>
            <p className="font-semibold">{formatCurrency(lastEntry.upper)}</p>
          </div>
        </div>
      )}

      {(showStatistics || snapshot.timestamp) && (
        <div className="text-xs text-muted-foreground">
          {showStatistics && (
            <div>
              {t.expectedReturn}: {formatPercent(snapshot.expectedReturn)} • {t.volatility}:{" "}
              {formatPercent(snapshot.volatility)}
            </div>
          )}
          {snapshot.timestamp && (
            <div>{t.snapshotTaken(getRelativeTime(snapshot.timestamp, language))}</div>
          )}
        </div>
      )}
    </div>
  );
}
